import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Lock, Loader2, LogOut, ArrowLeft } from 'lucide-react';
import { AdminDashboard } from '../components/admin/AdminDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'hh_admin_token';

const formatDetail = (d) => {
  if (!d) return 'Etwas ist schiefgelaufen.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return d.msg || String(d);
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); })
      .finally(() => setChecking(false));
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      setToken(data.token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setUser(null); };

  if (checking) {
    return <div className="min-h-screen bg-[#0B1E44] flex items-center justify-center text-white" data-testid="admin-loading"><Loader2 className="w-8 h-8 animate-spin text-[#E8B547]" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B1E44] relative flex items-center justify-center p-5">
        <div className="absolute inset-0 dotted-bg opacity-15" aria-hidden />
        <form onSubmit={login} className="relative w-full max-w-sm bg-white rounded-2xl p-7 shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="admin-login-form">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Die Haushelden" className="h-14 w-auto" />
            <div>
              <div className="text-[#0B1E44] font-extrabold text-lg leading-tight">Anfragen-Bereich</div>
              <div className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Nur für Mitarbeiter</div>
            </div>
          </div>
          <label className="block mt-6 text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">E-Mail</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-12 px-4 rounded-lg bg-[#FAF7F2] border border-slate-200 focus:border-[#B8892A] focus:ring-2 focus:ring-[#E0A72C]/30 outline-none text-[#0B1E44]" data-testid="admin-email" />
          <label className="block mt-4 text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Passwort</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-12 px-4 rounded-lg bg-[#FAF7F2] border border-slate-200 focus:border-[#B8892A] focus:ring-2 focus:ring-[#E0A72C]/30 outline-none text-[#0B1E44]" data-testid="admin-password" />
          {error && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="admin-login-error">{error}</div>}
          <button type="submit" disabled={loading} className="mt-6 w-full btn-gold h-12 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70" data-testid="admin-login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Anmelden
          </button>
          <Link to="/" className="mt-5 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B1E44]"><ArrowLeft className="w-3 h-3" /> Zur Website</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EFE7]" data-testid="admin-page">
      <header className="bg-[#0B1E44] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Die Haushelden" className="h-14 w-auto" />
            <div className="leading-tight">
              <div className="font-extrabold">Die Haushelden</div>
              <div className="text-[#E8B547] text-[10px] uppercase tracking-[0.2em] font-bold">Anfragen-Übersicht</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-white/70" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
          </div>
        </div>
      </header>
      <AdminDashboard token={token} onUnauthorized={logout} />
    </div>
  );
}
